import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { searchPatients } from '../../../api/claimsClient';
import Input from './Input';
import Icon from './Icon';

const SEARCH_DEBOUNCE_MS = 250;

// Derive a display MRN — stored value when present, otherwise a deterministic
// synthetic "MRN-XXXXXX" from the patient id (matches PatientsManagement.js).
function deriveMrn(p) {
  if (p.mrn) return p.mrn;
  // Use the real patient_id only — never the snapshot row's own id (p.id on a
  // claim's patient snapshot is the snapshot PK, not the patient), which would
  // render an MRN that matches no patient. Returns '' when there is no patient.
  const pid = p.patient_id || '';
  return pid ? 'MRN-' + pid.replaceAll('-', '').slice(0, 6).toUpperCase() : '';
}

/**
 * @param {{
 *   value: string|null,
 *   patientSnapshot: object|null,
 *   onSelect: (patient: object|null) => void,
 *   onCreateNew: () => void,
 *   density?: 'default'|'paper',
 *   disabled?: boolean,
 * }} props
 */
export default function PatientPicker({
  value, patientSnapshot, onSelect, onCreateNew,
  density = 'default', disabled = false,
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  /** @type {[any[], React.Dispatch<any>]} */
  const [results, setResults] = useState(/** @type {any[]} */ ([]));
  const [searchError, setSearchError] = useState('');
  /** @type {React.MutableRefObject<any>} */
  const inputRef = useRef(null);
  /** @type {[any, React.Dispatch<any>]} */
  const [rect, setRect] = useState(/** @type {any} */ (null));

  // Show the patient chip whenever a snapshot is present — either the normal
  // path (patient_id + snapshot) or the snapshot-only path that occurs on
  // cloned claims where the patient FK was not propagated but the snapshot
  // rows were copied correctly.
  const showChip = patientSnapshot && (patientSnapshot.first_name || patientSnapshot.last_name || value);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const id = setTimeout(() => {
      setSearchError('');
      searchPatients(query)
        .then((next) => {
          if (cancelled) return;
          setResults(next);
        })
        .catch((err) => {
          if (cancelled) return;
          setResults([]);
          const status = err?.response?.status;
          setSearchError(
            status === 401
              ? 'Patient search needs a fresh sign-in.'
              : 'Patient search is temporarily unavailable.',
          );
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [query, open]);

  useEffect(() => {
    if (!open) return undefined;
    function update() {
      if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
    }
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(/** @type {any} */ e) {
      if (inputRef.current && !inputRef.current.contains(e.target) && !e.target.closest?.('[data-picker-dropdown]')) {
        setOpen(false);
      }
    }
    window.addEventListener('mousedown', onDoc);
    return () => window.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (showChip) {
    /** @type {any} */
    const snap = patientSnapshot;
    const dob = snap.date_of_birth ? ` · DOB ${snap.date_of_birth}` : '';
    const mrn = deriveMrn(snap);
    return (
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '6px 10px',
        border: '1px solid var(--ab-fg)',
        borderRadius: 'var(--ab-radius-sm)',
        background: 'var(--ab-bg)',
        fontSize: 'var(--ab-text-sm)',
      }}>
        <span>{snap.first_name} {snap.last_name}{dob}{mrn ? ` · MRN ${mrn}` : ''}</span>
        {!disabled && (
          <button
            type="button"
            aria-label="Clear patient"
            onClick={() => onSelect(null)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'inline-flex' }}
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div ref={inputRef}>
        <Input
          placeholder="Search patients by name, MRN, DOB, phone, or email"
          value={query}
          disabled={disabled}
          onFocus={() => !disabled && setOpen(true)}
          onChange={(/** @type {any} */ e) => setQuery(e.target.value)}
        />
      </div>
      {open && rect && createPortal(
        <div
          data-picker-dropdown
          style={{
            position: 'fixed',
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            maxHeight: 280,
            overflow: 'auto',
            background: 'var(--ab-bg)',
            border: '1px solid var(--ab-fg)',
            borderRadius: 'var(--ab-radius-sm)',
            boxShadow: 'var(--ab-shadow-md)',
            zIndex: 1200,
          }}
        >
          {searchError && (
            <div style={{ padding: 12, fontSize: 'var(--ab-text-xs)', color: 'var(--ab-denied)' }}>
              {searchError}
            </div>
          )}
          {!searchError && results.length === 0 && (
            <div style={{ padding: 12, fontSize: 'var(--ab-text-xs)', color: 'var(--ab-fg-subtle)' }}>
              {query ? 'No matches.' : 'Start typing to search.'}
            </div>
          )}
          {results.map((p) => {
            const sub = [deriveMrn(p), p.date_of_birth, p.phone || p.email].filter(Boolean).join(' · ');
            return (
              <button
                key={p.patient_id || p.id}
                type="button"
                onClick={() => { onSelect(p); setOpen(false); setQuery(''); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '8px 12px',
                  background: 'transparent',
                  color: 'var(--ab-fg)',
                  border: 'none', cursor: 'pointer',
                  borderBottom: '1px solid var(--ab-border-hair)',
                }}
              >
                <div style={{ fontSize: 'var(--ab-text-sm)', fontWeight: 'var(--ab-weight-semi)' }}>
                  {p.first_name} {p.last_name}
                </div>
                {sub && (
                  <div style={{ fontSize: 'var(--ab-text-xs)', color: 'var(--ab-fg-subtle)' }}>{sub}</div>
                )}
              </button>
            );
          })}
          {!disabled && (
            <button
              type="button"
              onClick={() => { onCreateNew(); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', textAlign: 'left',
                padding: '10px 12px',
                background: 'var(--ab-bg-alt)',
                color: 'var(--ab-fg)',
                border: 'none', cursor: 'pointer',
                fontSize: 'var(--ab-text-sm)',
                fontWeight: 'var(--ab-weight-semi)',
                position: 'sticky', bottom: 0,
              }}
            >
              <Icon name="user-plus" size={14} /> Create new patient
            </button>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
